import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  FormLabelProps,
} from "@chakra-ui/react";
import { Field, FieldProps, FormikHelpers, setIn } from "formik";
import { EditorState, LexicalEditor, SerializedEditorState } from "lexical";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isNonEmptyString } from "src/services/predicates";
import Editor, {
  EditorRef,
  EditorVersion,
  MaybeStringifiedEditorState,
} from "./Editor";
import { SettingsContext, useSettings } from "./context/SettingsContext";
import { Settings } from "./context/SettingsContext/settings";
import { useContentConversion } from "./hooks/useContentConversion";
import { isSerializedEditorStateEqual } from "./utils/isSerializedEditorStateEqual";
import { $markTextNodesDirty } from "./utils/markTextNodesDirty";

type EditorStateUpdaterProps = {
  format: "html" | "json" | "plain";
  editor: LexicalEditor | null;
  fieldValue: MaybeStringifiedEditorState;
  fieldName: string;
  isInternalUpdateRef: React.MutableRefObject<boolean>;
  isInitializingEditorState: React.MutableRefObject<boolean>;
  onEditorStateInitialized: (editorState: EditorState) => void;
  onFieldValueChange?: (
    editorState: EditorState,
    serializedValue: SerializedEditorState | string
  ) => void;
};
// Component to handle editor state updates when field value changes
const EditorStateUpdater = ({
  format = "json",
  editor,
  fieldValue,
  fieldName,
  isInternalUpdateRef,
  isInitializingEditorState,
  onEditorStateInitialized,
  onFieldValueChange,
}: EditorStateUpdaterProps) => {
  const { settings } = useSettings();
  const previousValueRef = useRef<MaybeStringifiedEditorState | undefined>(
    undefined
  );

  const contentConversion = useContentConversion(settings);
  const setEditorStateFromValue = useCallback(
    (editor: LexicalEditor, value: MaybeStringifiedEditorState): void => {
      isInitializingEditorState.current = true;
      switch (format) {
        case "html":
        case "plain": {
          if (typeof value !== "string") {
            throw new Error(
              `formik value must be string if format is '${format}'`
            );
          }
          if (format === "html") {
            contentConversion.fromHtml(editor, value);
          } else {
            contentConversion.fromPlainText(editor, value);
          }
          break;
        }
        default:
          {
            const newEditorState = editor.parseEditorState(fieldValue);
            editor.setEditorState(newEditorState);
          }

          break;
      }
      queueMicrotask(() => {
        const editorState = editor.getEditorState();
        if (!settings.enableAutofocus) {
          editor.blur();
        }
        onEditorStateInitialized(editorState);
        onFieldValueChange?.(editorState, fieldValue);
        isInitializingEditorState.current = false;
      });
    },
    [
      contentConversion,
      fieldValue,
      format,
      isInitializingEditorState,
      onEditorStateInitialized,
      onFieldValueChange,
      settings.enableAutofocus,
    ]
  );

  useEffect(() => {
    if (!editor || !fieldValue) return;

    // Skip update if this change was triggered by our own blur handler
    // to prevent circular updates
    if (isInternalUpdateRef.current) {
      isInternalUpdateRef.current = false;
      previousValueRef.current = fieldValue;
      return;
    }

    // Only update if the value actually changed
    if (fieldValue === previousValueRef.current) return;

    try {
      // Update the editor state - defer to avoid flushSync during render
      // Use queueMicrotask to defer the update until after the current render cycle
      // otherwise it will cause a flushSync during render
      queueMicrotask(() => {
        setEditorStateFromValue(editor, fieldValue);
      });

      // Defer to next microtask to ensure setEditorState fully completes
      // Then mark all VariableNodes dirty to trigger transforms
      queueMicrotask(() => {
        editor.update(() => {
          $markTextNodesDirty();
        });
      });

      previousValueRef.current = fieldValue;
    } catch (error) {
      // If parsing fails, ignore the update (might be empty string or invalid JSON)
      console.warn(
        `Failed to update editor state for field ${fieldName}:`,
        error
      );
    }
  }, [
    editor,
    fieldValue,
    fieldName,
    isInternalUpdateRef,
    setEditorStateFromValue,
  ]);

  return null;
};

export type Props = {
  format?: "html" | "json" | "plain";
  name: string;
  width?: string;
  height?: string;
  label?: React.ReactNode;
  labelProps?: FormLabelProps;
  placeholder?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  isReadOnly?: boolean;
  editorRef?: EditorRef;
  settings?: Partial<Settings>;
  editorVersion?: EditorVersion;
  onFieldValueChange?: (
    editorState: EditorState,
    serializedValue: SerializedEditorState | string
  ) => void;
  helperText?: React.ReactNode;
  maxLength?: number;
};

const FormikLexicalEditor = ({
  settings: initialSettings,
  ...props
}: Props) => {
  return (
    <SettingsContext initialSettings={initialSettings}>
      <FormikEditor {...props} />
    </SettingsContext>
  );
};

const FormikEditor = ({
  format = "json",
  name,
  width,
  height,
  label,
  labelProps,
  placeholder,
  isDisabled = false,
  isRequired = false,
  isReadOnly = false,
  editorRef,
  editorVersion,
  onFieldValueChange,
  helperText,
  maxLength,
}: Omit<Props, "settings">) => {
  const { settings } = useSettings();
  const { htmlFieldName, plainFieldName, jsonFieldName } = settings;
  const [editor, setEditor] = useState<LexicalEditor | null>(null);
  // Track if the field value change is from our own blur/change handler to prevent circular updates
  const isInternalUpdateRef = useRef<boolean>(false);
  const isInitializingEditorStateRef = useRef<boolean>(false);
  const contentConversion = useContentConversion(settings);

  const createSerializedValues = useCallback(
    (editorState: EditorState) => {
      if (!editor) {
        throw new Error("Editor is not available");
      }

      let html: string | undefined;
      let plain: string | undefined;
      let json: SerializedEditorState | undefined;

      return {
        html: () => {
          html ??= contentConversion.toHtml(editor, editorState);
          return html;
        },
        plain: () => {
          plain ??= contentConversion.toPlainText(editor, editorState);
          return plain;
        },
        json: () => {
          json ??= editorState.toJSON();
          return json;
        },
      };
    },
    [contentConversion, editor]
  );

  const syncToFormik = useMemo(
    () =>
      debounce(
        async (
          formikHelpers: FormikHelpers<unknown>,
          editorState: EditorState,
          currentValue: unknown,
          currentValues: unknown
        ) => {
          const serializedValues = createSerializedValues(editorState);
          let serializedValue: SerializedEditorState | string;
          let nextValues = currentValues;
          switch (format) {
            case "html": {
              serializedValue = serializedValues.html();
              break;
            }
            case "plain": {
              serializedValue = serializedValues.plain();
              break;
            }
            case "json": {
              serializedValue = serializedValues.json();
              break;
            }
          }

          const primaryValueChanged = !isSerializedEditorStateEqual(
            serializedValue,
            currentValue
          );
          if (primaryValueChanged) {
            isInternalUpdateRef.current = true;
          }

          if (primaryValueChanged && settings.clearErrorsOnChange) {
            formikHelpers.setFieldError(name, undefined);
          }

          if (primaryValueChanged) {
            await formikHelpers.setFieldValue(name, serializedValue, false);
            nextValues = setIn(nextValues, name, serializedValue);
          }
          if (isNonEmptyString(plainFieldName)) {
            const plainValue = serializedValues.plain();
            await formikHelpers.setFieldValue(
              plainFieldName,
              plainValue,
              false
            );
            nextValues = setIn(nextValues, plainFieldName, plainValue);
          }
          if (isNonEmptyString(htmlFieldName)) {
            const htmlValue = serializedValues.html();
            await formikHelpers.setFieldValue(htmlFieldName, htmlValue, false);
            nextValues = setIn(nextValues, htmlFieldName, htmlValue);
          }
          if (isNonEmptyString(jsonFieldName)) {
            const jsonValue = serializedValues.json();
            await formikHelpers.setFieldValue(jsonFieldName, jsonValue, false);
            nextValues = setIn(nextValues, jsonFieldName, jsonValue);
          }

          if (
            primaryValueChanged ||
            isNonEmptyString(plainFieldName) ||
            isNonEmptyString(htmlFieldName) ||
            isNonEmptyString(jsonFieldName)
          ) {
            await formikHelpers.validateForm(nextValues);
          }

          if (primaryValueChanged) {
            onFieldValueChange?.(editorState, serializedValue);
          }
        },
        200
      ),
    [
      createSerializedValues,
      format,
      htmlFieldName,
      jsonFieldName,
      name,
      onFieldValueChange,
      plainFieldName,
      settings?.clearErrorsOnChange,
    ]
  );

  useEffect(() => () => syncToFormik.cancel(), [syncToFormik]);

  const initializeDerivedFields = useCallback(
    async (editorState: EditorState, formik: FieldProps) => {
      const serializedValues = createSerializedValues(editorState);

      if (
        isNonEmptyString(plainFieldName) &&
        formik.form.getFieldMeta(plainFieldName).value !==
          serializedValues.plain()
      ) {
        await formik.form.setFieldValue(
          plainFieldName,
          serializedValues.plain(),
          false
        );
      }
      if (
        isNonEmptyString(htmlFieldName) &&
        formik.form.getFieldMeta(htmlFieldName).value !==
          serializedValues.html()
      ) {
        await formik.form.setFieldValue(
          htmlFieldName,
          serializedValues.html(),
          false
        );
      }
      if (
        isNonEmptyString(jsonFieldName) &&
        JSON.stringify(formik.form.getFieldMeta(jsonFieldName).value) !==
          JSON.stringify(serializedValues.json())
      ) {
        await formik.form.setFieldValue(
          jsonFieldName,
          serializedValues.json(),
          false
        );
      }
    },
    [createSerializedValues, htmlFieldName, jsonFieldName, plainFieldName]
  );

  const handleOnBlur = useCallback(
    async (editorState: EditorState, formik: FieldProps) => {
      syncToFormik.cancel();
      const serializedValues = createSerializedValues(editorState);
      const currentValue = formik.field.value;

      let serializedValue: SerializedEditorState | string;
      switch (format) {
        case "html": {
          serializedValue = serializedValues.html();
          break;
        }
        case "plain": {
          serializedValue = serializedValues.plain();
          break;
        }
        case "json": {
          serializedValue = serializedValues.json();
          break;
        }
      }

      if (!isSerializedEditorStateEqual(serializedValue, currentValue)) {
        isInternalUpdateRef.current = true;
        await formik.form.setFieldValue(name, serializedValue, false);
        await formik.form.setFieldTouched(name, true, false);

        if (isNonEmptyString(plainFieldName)) {
          await formik.form.setFieldValue(
            plainFieldName,
            serializedValues.plain(),
            false
          );
        }
        if (isNonEmptyString(htmlFieldName)) {
          await formik.form.setFieldValue(
            htmlFieldName,
            serializedValues.html(),
            false
          );
        }
        if (isNonEmptyString(jsonFieldName)) {
          await formik.form.setFieldValue(
            jsonFieldName,
            serializedValues.json(),
            false
          );
        }
        onFieldValueChange?.(editorState, serializedValue);
      }
    },
    [
      createSerializedValues,
      format,
      htmlFieldName,
      jsonFieldName,
      name,
      onFieldValueChange,
      plainFieldName,
      syncToFormik,
    ]
  );

  const handleOnChange = useCallback(
    (editorState: EditorState, formik: FieldProps) => {
      if (isInitializingEditorStateRef.current) return;
      syncToFormik(
        formik.form,
        editorState,
        formik.field.value,
        formik.form.values
      );
    },
    [syncToFormik]
  );

  const handleEditorRef = useCallback(
    (nextEditor: LexicalEditor) => {
      setEditor(nextEditor);
      if (editorRef) {
        editorRef(nextEditor);
      }
    },
    [editorRef]
  );

  return (
    <Field name={name}>
      {(formik: FieldProps) => {
        return (
          <FormControl
            isRequired={isRequired}
            isInvalid={!formik.form.isValid}
            isDisabled={isDisabled}
            isReadOnly={isReadOnly}
          >
            {label && <FormLabel {...labelProps}>{label}</FormLabel>}
            <Editor
              maxLength={maxLength}
              id={name}
              name={name}
              placeholder={placeholder}
              height={height}
              width={width}
              isReadOnly={isReadOnly || isDisabled}
              onBlur={(editor) => handleOnBlur(editor, formik)}
              onChange={(editorState) => handleOnChange(editorState, formik)}
              editorRef={handleEditorRef}
              editorBorderColor={formik.meta.error ? "red.500" : undefined}
              editorVersion={editorVersion}
            />
            <EditorStateUpdater
              format={format}
              editor={editor}
              fieldValue={formik.field.value}
              fieldName={name}
              isInternalUpdateRef={isInternalUpdateRef}
              isInitializingEditorState={isInitializingEditorStateRef}
              onEditorStateInitialized={(editorState) =>
                initializeDerivedFields(editorState, formik)
              }
              onFieldValueChange={onFieldValueChange}
            />
            {formik.meta.error ? (
              <FormErrorMessage>{formik.meta.error}</FormErrorMessage>
            ) : helperText ? (
              <FormHelperText>{helperText}</FormHelperText>
            ) : null}
          </FormControl>
        );
      }}
    </Field>
  );
};

export default FormikLexicalEditor;
